import { events, EventDefinition, EventHandler } from "./events";
import { Middleware, MiddlewareFunc } from "./middleware";
import { Message } from "./message";

abstract class ChannelEvent<TMessage extends Message> implements EventDefinition {
  // eslint-disable-next-line
  constructor(public readonly channel: Channel<TMessage>) { }
}

export class ChannelCloseEvent<TMessage extends Message>
  extends ChannelEvent<TMessage> {
}

export type ChannelEvents<TMessage extends Message> = {
  "close": ChannelCloseEvent<TMessage>;
};

export type ChannelMiddleware<TMessage extends Message> = MiddlewareFunc<TMessage>;

export type ChannelMessageCallback<TMessage extends Message> = (message: TMessage) => Promise<void> | void;

export type ChannelSubscription = {
  unsubscribe: () => void;
}

// eslint-disable-next-line
export interface Channel<TMessage extends Message = any> {
  close: () => Promise<void> | void;

  use: (middleware: ChannelMiddleware<TMessage>) => Channel<TMessage>;

  subscribe: (
    pattern: string | RegExp,
    callback: ChannelMessageCallback<TMessage>,
  ) => ChannelSubscription;

  publish: (message: TMessage) => Promise<void> | void;

  on: <K extends keyof ChannelEvents<TMessage>>(
    event: K,
    eventHandler: EventHandler<ChannelEvents<TMessage>[K]>,
  ) => void;

  once: <K extends keyof ChannelEvents<TMessage>>(
    event: K,
    eventHandler: EventHandler<ChannelEvents<TMessage>[K]>,
  ) => void;
}

class ChannelImpl<TMessage extends Message> implements Channel<TMessage> {
  private events_ = events<ChannelEvents<TMessage>>();

  private middleware_ = new Middleware<TMessage>();

  private subscribers_: {
    pattern: RegExp;
    callback: ChannelMessageCallback<TMessage>;
  }[] = [];

  close() {
    this.events_.dispatch("close", new ChannelCloseEvent(this));
  }

  use(middleware: ChannelMiddleware<TMessage>) {
    this.middleware_.use(middleware);

    return this;
  }

  subscribe(
    pattern: string | RegExp,
    callback: (message: TMessage) => Promise<void> | void,
  ) {
    const subscription = {
      pattern: typeof pattern === "string"
        ? new RegExp(`^${pattern}$`)
        : pattern,
      callback,
    };

    this.subscribers_.push(subscription);

    return {
      unsubscribe: () => {
        this.subscribers_ = this.subscribers.filter((s) => s !== subscription);
      },
    } as ChannelSubscription;
  }

  async publish(message: TMessage) {
    const processedMessage = await this.middleware_.apply(message);

    await this.dispatch(processedMessage);
  }

  on<K extends keyof ChannelEvents<TMessage>>(
    event: K,
    eventHandler: EventHandler<ChannelEvents<TMessage>[K]>,
  ) {
    this.events_.on(event, eventHandler);

    return this;
  }

  once<K extends keyof ChannelEvents<TMessage>>(
    event: K,
    eventHandler: EventHandler<ChannelEvents<TMessage>[K]>,
  ) {
    this.events_.once(event, eventHandler);

    return this;
  }

  protected get events() {
    return this.events_;
  }

  protected get middleware() {
    return this.middleware_;
  }

  protected get subscribers() {
    return this.subscribers_;
  }

  protected subscribersForMessage(message: TMessage) {
    return this.subscribers_.filter((s) =>
      message.match(s.pattern)
    )
  }

  protected async dispatch(message: TMessage) {
    const subscribers = this.subscribersForMessage(message);

    await Promise.all(subscribers.map(s => s.callback(message))
    );
  }
}

class ChannelWithOptionsImpl<TMessage extends Message, TOptions>
  extends ChannelImpl<TMessage> {
  private options_: TOptions | undefined;

  constructor(options?: TOptions) {
    super();

    this.options_ = options;
  }

  get options() {
    return this.options_;
  }
}

export class DefaultChannel<TMessage extends Message, TOptions = never>
  extends ChannelWithOptionsImpl<TMessage, TOptions> { }

export const createChannel = <TMessage extends Message>() =>
  new DefaultChannel<TMessage>();
